/**
 * Check if two date ranges overlap
 * @param {Date|string} start1 - Start date of first range
 * @param {Date|string} end1 - End date of first range
 * @param {Date|string} start2 - Start date of second range
 * @param {Date|string} end2 - End date of second range
 * @returns {boolean} - True if ranges overlap
 */
export const doRangesOverlap = (start1, end1, start2, end2) => {
    if (!start1 || !end1 || !start2 || !end2) return false;

    const s1 = new Date(start1);
    const e1 = new Date(end1);
    const s2 = new Date(start2);
    const e2 = new Date(end2);

    // Check if dates are valid
    if (isNaN(s1) || isNaN(e1) || isNaN(s2) || isNaN(e2)) return false;

    // Ranges overlap if: start1 < end2 AND start2 < end1
    return s1 < e2 && s2 < e1;
};

/**
 * Get all episodes that overlap with a given episode
 * @param {Object} episode - The episode to check
 * @param {Array} allEpisodes - Array of all episodes
 * @returns {Array} - Array of overlapping episodes
 */
export const getOverlappingEpisodes = (episode, allEpisodes) => {
    if (!episode.start_date_time || !episode.end_date_time) return [];

    return allEpisodes.filter(other => {
        // Don't compare with self
        if (other.id === episode.id) return false;

        // Check if ranges overlap
        return doRangesOverlap(
            episode.start_date_time,
            episode.end_date_time,
            other.start_date_time,
            other.end_date_time
        );
    });
};

/**
 * Get all episodes scheduled for a specific date
 * @param {Date} date - The date to check
 * @param {Array} episodes - Array of all episodes
 * @returns {Array} - Array of episodes on that date
 */
export const getEpisodesForDate = (date, episodes) => {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    const nextDay = new Date(targetDate);
    nextDay.setDate(nextDay.getDate() + 1);

    return episodes.filter(episode => {
        if (!episode.start_date_time || !episode.end_date_time) return false;

        const start = new Date(episode.start_date_time);
        const end = new Date(episode.end_date_time);

        // Check if episode spans this date
        return start < nextDay && end > targetDate;
    });
};

/**
 * Check if any episodes on a date have overlaps
 * @param {Date} date - The date to check
 * @param {Array} episodes - Array of all episodes
 * @returns {boolean} - True if there are overlaps on this date
 */
export const dateHasOverlaps = (date, episodes) => {
    const episodesOnDate = getEpisodesForDate(date, episodes);

    for (let i = 0; i < episodesOnDate.length; i++) {
        const overlaps = getOverlappingEpisodes(episodesOnDate[i], episodes);
        if (overlaps.length > 0) return true;
    }

    return false;
};

/**
 * Get episodes that START on a specific date (not checking end date)
 * @param {Date} date - The date to check
 * @param {Array} episodes - Array of all episodes
 * @returns {Array} - Array of episodes that start on that date
 */
export const getEpisodesStartingOnDate = (date, episodes) => {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    return episodes.filter(episode => {
        if (!episode.start_date_time) return false;

        const start = new Date(episode.start_date_time);
        const startDateOnly = new Date(start);
        startDateOnly.setHours(0, 0, 0, 0);

        return startDateOnly.getTime() === targetDate.getTime();
    });
};